import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { GameSummary } from '@replay/shared';
import { api } from '../lib/api.ts';
import { systemAccent, systemName, systemShort } from '../lib/format.ts';
import { ambientFor, previewAmbient } from './Ambient.tsx';
import { Icon } from './Icon.tsx';
import { useToast } from './ui.tsx';

type ArtGame = Pick<GameSummary, 'title' | 'systemId' | 'coverUrl'> & { coverHasTitle?: boolean };

/**
 * Box art (or a generated case when the game has no cover yet). `plain` never
 * draws the title on top: for thumbnails too small for it to be legible.
 */
export function Cover({ game, eager, plain }: { game: ArtGame; eager?: boolean; plain?: boolean }) {
  return (
    <div className="box-art" style={{ ['--sys' as string]: systemAccent(game.systemId) }}>
      {game.coverUrl ? (
        <>
          <img src={game.coverUrl} alt="" loading={eager ? 'eager' : 'lazy'} decoding="async" draggable={false} />
          {game.coverHasTitle === false && !plain && (
            <div className="box-replay-title" aria-hidden="true">
              <span className="ttl">{game.title}</span>
            </div>
          )}
        </>
      ) : (
        <div className="box-gen" aria-hidden="true">
          <div className="band">
            <span>{systemShort(game.systemId)}</span>
            <i />
          </div>
          <span className="mono">
            {game.title
              .replace(/[^\p{L}\p{N}]/gu, '')
              .slice(0, 2)
              .toUpperCase()}
          </span>
          {!plain && <span className="ttl">{game.title}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * A focusable game in a rail or grid. Focus/hover previews its art on the
 * stage; the gamepad's Y button toggles it as a favorite.
 */
export function GameCard({ game, subtitle, progress, autoFocus }: { game: GameSummary; subtitle?: string; progress?: number; autoFocus?: boolean }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onNav = async (e: Event) => {
      const action = (e as CustomEvent<{ action: string }>).detail.action;
      if (action !== 'tertiary') return;
      e.preventDefault();
      const on = !game.isFavorite;
      try {
        await api(`/api/games/${game.id}/favorite`, { method: on ? 'PUT' : 'DELETE' });
        toast(on ? `${game.title} añadido a favoritos` : `${game.title} quitado de favoritos`, 'ok');
        qc.invalidateQueries({ queryKey: ['home'] });
        qc.invalidateQueries({ queryKey: ['games'] });
        qc.invalidateQueries({ queryKey: ['game'] });
      } catch {
        toast('No se pudo actualizar favoritos', 'error');
      }
    };
    el.addEventListener('replay:nav', onNav);
    return () => el.removeEventListener('replay:nav', onNav);
  }, [game.id, game.isFavorite, game.title, qc, toast]);

  const meta = subtitle ?? `${systemShort(game.systemId)}${game.year ? ` · ${game.year}` : ''}${game.isHack ? ' · Hack' : ''}`;
  return (
    <Link
      ref={ref}
      to={`/game/${game.slug}`}
      className="box"
      aria-label={`${game.title}, ${systemName(game.systemId)}`}
      onFocus={() => previewAmbient(ambientFor(game, systemAccent))}
      onMouseEnter={() => previewAmbient(ambientFor(game, systemAccent))}
      data-autofocus={autoFocus ? '' : undefined}
      draggable={false}
    >
      <Cover game={game} />
      <div className="box-badges">
        {game.availability === 'unavailable' && <span className="tag">No disponible</span>}
        {game.isFavorite && (
          <span className="fav" aria-hidden="true">
            <Icon name="heart" />
          </span>
        )}
      </div>
      {progress != null && (
        <div className="box-progress" aria-hidden="true">
          <i style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}
      <div className="box-meta">
        <strong>{game.title}</strong>
        <span>{meta}</span>
      </div>
    </Link>
  );
}

/** Horizontal carousel. One navigation row: left/right stays inside, up/down remembers position. */
export function Shelf({ games, subtitle, label }: { games: GameSummary[]; subtitle?: (g: GameSummary) => string | undefined; label?: string }) {
  return (
    <div className="rail" role="list" aria-label={label} data-nav-row>
      {games.map((g) => (
        <div role="listitem" key={g.id}>
          <GameCard game={g} subtitle={subtitle?.(g)} />
        </div>
      ))}
    </div>
  );
}

export function CoverGrid({ games }: { games: GameSummary[] }) {
  return (
    <div className="lib-grid" role="list" data-nav-grid>
      {games.map((g, i) => (
        <div role="listitem" key={g.id}>
          <GameCard game={g} autoFocus={i === 0} />
        </div>
      ))}
    </div>
  );
}
